type Direction = 'previous' | 'next';

/** @internal */
export function getCurrentSuggestion(
  container: HTMLDivElement | null,
  currentSuggestionId?: string,
) {
  if (!container || !currentSuggestionId) {
    return null;
  }

  return container.querySelector<HTMLLIElement>(
    `[data-strato-filter-field-suggestion-id="${currentSuggestionId}"]`,
  );
}

/** @internal */
export function getFirstSuggestion(
  container: HTMLDivElement | null,
): HTMLElement | null {
  if (!container) {
    return null;
  }
  return container.querySelector<HTMLLIElement>(
    '[data-strato-filter-field-suggestion-id]',
  );
}

/** @internal */
export function getLastSuggestion(
  container: HTMLDivElement | null,
): HTMLElement | null {
  if (
    !container ||
    !container.querySelector<HTMLLIElement>(
      '[data-strato-filter-field-suggestion-id]',
    )
  ) {
    return null;
  }

  return getNthSibling(
    'previous',
    1,
    container,
    container.lastElementChild as HTMLElement,
  );
}

/**
 * Get the next suggestion in the given container element.
 * If direction is set to 'next', start at the first suggestion. Else, start at the last.
 * If a 'currentSuggestionId' is passed, start at the matching suggestion.
 * @internal
 */
export function getNextSuggestion(
  direction: Direction,
  container: HTMLDivElement | null,
  currentSuggestionId?: string,
  nthSibling: number = 1,
): HTMLElement | null {
  if (
    !container ||
    !container.querySelector<HTMLLIElement>(
      '[data-strato-filter-field-suggestion-id]',
    )
  ) {
    return null;
  }

  let currentSuggestion: HTMLElement | null = null;

  // Get the suggestion with the given id.
  if (currentSuggestionId) {
    currentSuggestion = getCurrentSuggestion(container, currentSuggestionId);
  }

  // If no suggestion has been found, return the first suggestion or start searching
  // at the last child of the container, depending on the direction.
  if (!currentSuggestion) {
    switch (direction) {
      case 'next': {
        return getFirstSuggestion(container);
      }
      case 'previous': {
        return getLastSuggestion(container);
      }
    }
  }

  // Recursively look for the next suggestion (suggestion may be nested).
  return getNthSibling(
    direction,
    nthSibling,
    container,
    currentSuggestion,
    nthSibling === 1, // we only want to cycle through when going in 1-step increments
  );
}

function getNthSibling(
  direction: Direction,
  nthSibling: number,
  container: HTMLDivElement | null,
  currentNode?: HTMLElement | null,
  circularNavigation: boolean = true,
): HTMLElement | null {
  while (currentNode) {
    if (currentNode.childElementCount > 0) {
      currentNode = (
        direction === 'next'
          ? currentNode.firstElementChild
          : currentNode.lastElementChild
      ) as HTMLElement;

      if (
        currentNode &&
        currentNode.dataset.stratoFilterFieldSuggestionId !== undefined
      ) {
        nthSibling = nthSibling - 1;
        if (nthSibling === 0) {
          return currentNode;
        }
      }

      return getNthSibling(
        direction,
        nthSibling,
        container,
        currentNode,
        circularNavigation,
      );
    }

    currentNode = _getNextSibling(direction, currentNode);

    if (currentNode?.dataset.stratoFilterFieldSuggestionId !== undefined) {
      nthSibling = nthSibling - 1;
      if (nthSibling === 0) {
        return currentNode as HTMLElement;
      } else {
        return getNthSibling(
          direction,
          nthSibling,
          container,
          currentNode,
          circularNavigation,
        );
      }
    }
  }

  // If the focus is on the last element and we want to go to the next one,
  // go to the first option if circularNavigation is enabled. Also go to the first one if
  // circularNavigation is deactivated and we want to go to a previous option
  if (
    (circularNavigation && direction === 'next') ||
    (!circularNavigation && direction === 'previous')
  ) {
    return getFirstSuggestion(container);
  }
  // If the focus is on the first element and we want to go to the previous one,
  // go to the last option if circularNavigation is enabled. Also go to the last one if
  // circularNavigation is deactivated and we want to go to the next option
  else if (
    (circularNavigation && direction === 'previous') ||
    (!circularNavigation && direction === 'next')
  ) {
    return getLastSuggestion(container);
  }

  return null;
}

/**
 * Get the next sibling, going back up the DOM if no next / previous element sibling
 * exists until we are at the root of the suggestions overlay.
 */
function _getNextSibling(
  direction: Direction,
  currentNode?: HTMLElement | null,
): HTMLElement | undefined {
  if (currentNode?.classList.contains('suggestions-overlay')) {
    return undefined;
  }

  if (currentNode) {
    const _tmpNode =
      direction === 'next'
        ? currentNode.nextElementSibling
        : currentNode.previousElementSibling;

    if (!_tmpNode) {
      return _getNextSibling(direction, currentNode.parentElement);
    }

    return _tmpNode as HTMLElement;
  }
}
